// src/pages/PortalTicketsPage.js
import React, { useState, useEffect } from 'react';
import portalApi from '../utils/portalApi';
import toast from 'react-hot-toast';
import { QRCodeSVG } from 'qrcode.react';

export default function PortalTicketsPage() {
  const [tickets, setTickets] = useState(null);
  const [view, setView] = useState('upcoming');

  useEffect(() => {
    portalApi.get('/portal/tickets')
      .then(res => setTickets(res.data))
      .catch(() => toast.error('Failed to load tickets.'));
  }, []);

  if (tickets === null) return <div className="loading-page"><div className="spinner" /></div>;

  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const isPast = (t) => t.event_date && new Date(t.event_date) < now;

  const filtered = view === 'upcoming'
    ? tickets.filter(t => !isPast(t))
    : tickets.filter(t => isPast(t));

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">My Tickets</h1>
          <p className="page-subtitle">Tickets you and your business have purchased</p>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className={`btn btn-sm ${view === 'upcoming' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setView('upcoming')}>Upcoming</button>
          <button className={`btn btn-sm ${view === 'past' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setView('past')}>Past</button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">🎟️</div>
          <h3>No {view} tickets</h3>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {filtered.map(t => (
            <div key={t.id} className="card" style={{ padding: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 240 }}>
                  <h3 style={{ fontSize: '1.05rem', marginBottom: 6 }}>{t.event_name}</h3>
                  <div style={{ display: 'flex', gap: 12, fontSize: '0.85rem', color: 'var(--gray-600)', flexWrap: 'wrap', marginBottom: 8 }}>
                    <span>📅 {fmtDate(t.event_date)}{t.start_time ? ` · ${fmtTime(t.start_time)}` : ''}</span>
                    {t.venue && <span>📍 {t.venue}</span>}
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: '0.8rem' }}>
                    <span className="badge" style={{ fontSize: '0.7rem' }}>{t.ticket_type}</span>
                    <span style={{ color: 'var(--gray-600)' }}>
                      {t.buyer_first_name} {t.buyer_last_name}
                    </span>
                    <span style={{ color: 'var(--gray-400)' }}>·</span>
                    <span className={`badge badge-${t.payment_status}`} style={{ fontSize: '0.7rem' }}>{t.payment_status}</span>
                    {t.checked_in && (
                      <span style={{ color: 'var(--success)', fontWeight: 600 }}>✓ Checked in</span>
                    )}
                  </div>
                  <div style={{ marginTop: 6, fontSize: '0.75rem', color: 'var(--gray-400)' }}>
                    Ticket #{t.ticket_number}
                  </div>
                </div>
                {!isPast(t) && t.qr_code_token && !t.checked_in && (
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ padding: 6, background: 'white', border: '1px solid var(--gray-200)', borderRadius: 6, display: 'inline-block' }}>
                      <QRCodeSVG
                        value={`${window.location.origin}/checkin/${t.qr_code_token}`}
                        size={104}
                        level="M"
                      />
                    </div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--gray-500)', marginTop: 4 }}>
                      Show at door
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}
function fmtTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hh = parseInt(h, 10);
  const ampm = hh >= 12 ? 'PM' : 'AM';
  const h12 = hh % 12 || 12;
  return `${h12}:${m} ${ampm}`;
}
